import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Calendar,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Table,
  Tag,
  Popconfirm,
  message,
  Space,
} from "antd";
import {
  CalendarOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { HRAction } from "../../Types";
import {
  useLeavesQuery,
  useCreateLeaveMutation,
  useDeleteLeaveMutation,
  useEmployeesQuery,
  useSectionAccess,
} from "../../Services";

const { Option } = Select;
const { TextArea } = Input;

export const LeaveCalendar: React.FC = () => {
  const [form] = Form.useForm();
  const [currentMonth, setCurrentMonth] = useState(dayjs().format("YYYY-MM"));
  const [searchQuery, setSearchQuery] = useState("");

  // Queries
  const { data: employees = [], isLoading: employeesLoading } =
    useEmployeesQuery({ page: 1, limit: 100 });
  const { data: leaves = [], isLoading: leavesLoading } = useLeavesQuery({
    month: currentMonth,
    search: searchQuery,
  });

  // Mutations
  const { isAllowed } = useSectionAccess("hr");

  const createLeaveMutation = useCreateLeaveMutation();
  const deleteLeaveMutation = useDeleteLeaveMutation();

  const handleMonthChange = (date: dayjs.Dayjs) => {
    setCurrentMonth(date.format("YYYY-MM"));
  };

  const handleFormSubmit = async (values: any) => {
    try {
      const payload = {
        employee_id: values.employee_id,
        leave_date: values.leave_date.format("YYYY-MM-DD"),
        leave_type: values.leave_type,
        notes: values.notes || "",
      };

      const res = await createLeaveMutation.mutateAsync(payload);
      if (res?.success) {
        message.success(res?.message || "Employee leave logged successfully.");
        form.resetFields();
      } else {
        message.error(res?.message || "Failed to log leave.");
      }
    } catch (err: any) {
      message.error(err?.message || "An error occurred while logging leave.");
    }
  };

  const handleDeleteLeave = async (id: string) => {
    try {
      const res = await deleteLeaveMutation.mutateAsync(id);
      if (res?.success) {
        message.success(res?.message || "Leave log deleted successfully.");
      } else {
        message.error(res?.message || "Failed to delete leave log.");
      }
    } catch (err: any) {
      message.error(err?.message || "An error occurred while deleting leave.");
    }
  };

  const getTagColor = (type: string) => {
    switch (type) {
      case "Casual":
        return "blue";
      case "Medical":
        return "red";
      case "Annual":
        return "green";
      case "Unpaid":
        return "orange";
      default:
        return "default";
    }
  };

  // Filter leaves for specific day
  const getListData = (dateStr: string) => {
    return leaves.filter((leave: any) => leave.leave_date === dateStr);
  };

  const cellRender = (current: dayjs.Dayjs, info: any) => {
    if (info.type === "date") {
      const dateStr = current.format("YYYY-MM-DD");
      const listData = getListData(dateStr);
      return (
        <div style={{ maxHeight: "75px", overflowY: "auto", margin: "2px 0" }}>
          {listData.map((item: any) => (
            <Tag
              key={item.id}
              color={getTagColor(item.leave_type)}
              style={{
                fontSize: "10px",
                width: "100%",
                margin: "1px 0",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {item.first_name} {item.last_name[0]}.
            </Tag>
          ))}
        </div>
      );
    }
    return info.originNode;
  };

  // Leaves table columns
  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.first_name} {record.last_name}
          </div>
          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>{record.nic}</div>
        </div>
      ),
    },
    {
      title: "Date / Type",
      key: "date_type",
      render: (_: any, record: any) => (
        <div>
          <div>{record.leave_date}</div>
          <Tag color={getTagColor(record.leave_type)}>{record.leave_type}</Tag>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete leave log?"
          description="Are you sure you want to delete this leave record?"
          onConfirm={() => handleDeleteLeave(record.id)}
          okText="Yes"
          cancelText="No"
        >
          {isAllowed(HRAction.ManageLeaves) && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deleteLeaveMutation.isPending}
            />
          )}
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div
        style={{ marginBottom: "24px", display: "flex", alignItems: "center" }}
      >
        <CalendarOutlined
          style={{ fontSize: "28px", marginRight: "12px", color: "#1890ff" }}
        />
        <div>
          <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 600 }}>
            Leave Calendar
          </h1>
          <p style={{ margin: 0, color: "#8c8c8c" }}>
            Log employee leave dates and view a visual summary on the monthly
            calendar.
          </p>
        </div>
      </div>

      <Row gutter={16}>
        {/* Left Side: Forms & History list */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {/* Log Leave Date Card */}
            <Card
              title="Log Leave Date"
              bordered={false}
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            >
              <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
                <Form.Item
                  name="employee_id"
                  label="Select Employee"
                  rules={[
                    { required: true, message: "Please select an employee" },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Search employee..."
                    optionFilterProp="children"
                    loading={employeesLoading}
                  >
                    {employees?.data?.map((emp: any) => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} (
                        {emp.designation || "No Designation"})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="leave_date"
                  label="Leave Date"
                  rules={[
                    { required: true, message: "Please select leave date" },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                  name="leave_type"
                  label="Leave Type"
                  initialValue="Casual"
                  rules={[
                    { required: true, message: "Please select leave type" },
                  ]}
                >
                  <Select>
                    <Option value="Casual">Casual Leave</Option>
                    <Option value="Medical">Medical Leave</Option>
                    <Option value="Annual">Annual Leave</Option>
                    <Option value="Unpaid">Unpaid Leave</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <TextArea placeholder="Optional remarks..." rows={2} />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  {isAllowed(HRAction.ManageLeaves) && (
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      icon={<PlusOutlined />}
                      loading={createLeaveMutation.isPending}
                    >
                      Log Leave
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </Card>

            {/* Leave History Table Card */}
            <Card
              title="Leave Logs"
              bordered={false}
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            >
              <Input
                placeholder="Search employee name or NIC..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                style={{ marginBottom: "16px" }}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />

              <Table
                dataSource={leaves}
                columns={columns}
                rowKey="id"
                size="small"
                loading={leavesLoading}
                pagination={{ pageSize: 5 }}
              />
            </Card>
          </Space>
        </Col>

        {/* Right Side: The Interactive Calendar */}
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)", height: "100%" }}
            bodyStyle={{ padding: "16px" }}
          >
            <Calendar
              cellRender={cellRender}
              onChange={handleMonthChange}
              onPanelChange={handleMonthChange}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
